import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { pickField } from '@/shein-platform/utils/sheinResponse';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import { normalizeImageUrl } from '@/shein-platform/modules/aftersales/returnOrderDisplay';
import FinanceListGoodsCell, { extractGoodsList } from '@/shein-platform/modules/finance/FinanceListGoodsCell';
import {
  BILL_TYPE_CHECK,
  BILL_TYPE_REPORT,
  displayFinanceValue,
  extractDetailLines,
  extractDetailPayload,
  extractRelatedCheckOrders,
  financeInOutLabel,
  financePaymentMethodLabel,
  financeSecondOrderTypeLabel,
  financeStatusLabel,
  financeStatusModifier,
  financeTabLabel,
  formatLedgerAmount,
  formatMoney,
  formatPayDate,
  hasFinanceRichDetail,
} from '@/shein-platform/modules/finance/financeDisplay';
import '@/styles/shein-platform/shein-product-ops.css';

function normalizeBillType(raw) {
  const t = String(raw || '').trim().toUpperCase();
  return t === BILL_TYPE_REPORT ? BILL_TYPE_REPORT : BILL_TYPE_CHECK;
}

function FinanceSkuGoodsCell({ line }) {
  const { t } = useTranslation('workbench');
  const title = pickField(line, 'goodsTitle', 'productName', 'spuName', 'title') || '';
  const merchantSku = pickField(line, 'supplierSku', 'sellerSku', 'merchantSku', 'supplier_sku') || '';
  const skc = pickField(line, 'skcName', 'skcCode', 'skc', 'platformSkcId') || '';
  const supplierCode = pickField(line, 'supplierCode', 'goodsSn', 'articleNo') || '';
  const img = normalizeImageUrl(pickField(line, 'imageUrl', 'mainImageUrl'));
  if (!title && !img && !skc && !merchantSku && !supplierCode) {
    return <span className="spo-return-product__empty">{t('finance.noGoods')}</span>;
  }
  return (
    <div className="spo-return-product">
      {img ? (
        <SheinProductThumb
          imageUrl={img}
          className="spo-return-product__img"
          placeholderClassName="spo-return-product__img spo-return-product__img--empty"
          alt={title || merchantSku || t('finance.goodsAlt')}
        />
      ) : (
        <div className="spo-return-product__img spo-return-product__img--empty" />
      )}
      <div className="spo-return-product__meta">
        <HoverCopyField
          value={title}
          className="spo-return-product__title"
          popoverCopy
        />
        <HoverCopyField
          label="SKC"
          value={skc}
          className="spo-return-product__sku"
        />
        <HoverCopyField
          label={t('certList.merchantSku')}
          value={merchantSku}
          className="spo-return-product__sku"
        />
        {supplierCode ? (
          <HoverCopyField
            label={t('tiktokCatalog.supplier')}
            value={supplierCode}
            className="spo-return-product__sku"
          />
        ) : null}
      </div>
    </div>
  );
}

/** 双列键值栅格，对齐售后详情键值表视觉 */
function FinanceKvGrid({ rows }) {
  const { t } = useTranslation('workbench');
  if (!rows.length) {
    return <p className="spo-return-detail__empty">{t('freightLib.noData')}</p>;
  }
  return (
    <dl className="spo-finance-kv">
      {rows.map((row) => (
        <div key={row.label} className="spo-finance-kv__item">
          <dt>{row.label}</dt>
          <dd>{displayFinanceValue(row.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function LinesTable({ columns, rows, emptyText, onRowAction, className = '' }) {
  if (!rows.length) {
    return <p className="spo-return-detail__empty">{emptyText}</p>;
  }
  const colCount = Math.max(columns.length, 1);
  const hasGoodsCol = columns.some((col) => col.key === 'goods');
  const defaultWidth = `${(100 / colCount).toFixed(4)}%`;
  return (
    <div className={`spo-return-detail__goods-wrap spo-finance-lines-wrap ${className}`.trim()}>
      <table className="spo-publish__table spo-return-detail__goods-table spo-finance-lines-table">
        <colgroup>
          {columns.map((col) => (
            <col
              key={col.key}
              style={{
                width: col.width
                  || (hasGoodsCol
                    ? (col.key === 'goods' ? '42%' : `${(58 / Math.max(colCount - 1, 1)).toFixed(4)}%`)
                    : defaultWidth),
              }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, idx) => (
            <tr key={pickField(line, 'id', 'checkOrderNo', 'skuCode', 'bzOrderNo') || idx}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={col.key === 'goods' ? 'spo-ops-td spo-ops-td--product' : undefined}
                >
                  {col.render(line, { onRowAction })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BillDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { billType: rawType, billNo: rawNo } = useParams();
  const billType = normalizeBillType(rawType);
  const billNo = decodeURIComponent(rawNo || '').trim();
  const isReport = billType === BILL_TYPE_REPORT;
  const { activeShopId, loading: shopLoading } = useSheinShop();

  const [detail, setDetail] = useState(null);
  const [relatedChecks, setRelatedChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const applyResponse = useCallback((res) => {
    const payload = extractDetailPayload(res);
    setDetail(payload);
    setRelatedChecks(extractRelatedCheckOrders(res, payload));
  }, []);

  const loadDetail = useCallback(async () => {
    if (!activeShopId || !billNo) return;
    setLoading(true);
    setError('');
    try {
      try {
        const cached = await sheinPlatformAPI.getCachedFinanceBillDetail(activeShopId, billType, billNo);
        if (hasFinanceRichDetail(cached) || extractDetailPayload(cached)) {
          applyResponse(cached);
          return;
        }
      } catch {
        /* 本地无缓存再拉平台 */
      }

      const live = await sheinPlatformAPI.getFinanceBillDetail(activeShopId, {
        billType,
        checkOrderNo: isReport ? undefined : billNo,
        reportOrderNo: isReport ? billNo : undefined,
        billNo,
      });
      applyResponse(live);
    } catch (e) {
      setDetail(null);
      setRelatedChecks([]);
      setError(e?.message || t('finance.loadDetailFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, applyResponse, billNo, billType, isReport, t]);

  useEffect(() => {
    if (shopLoading) return;
    if (!activeShopId) {
      setError(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }));
      return;
    }
    if (!billNo) {
      setError(t('finance.badBillNo'));
      return;
    }
    loadDetail();
  }, [activeShopId, billNo, billType, shopLoading, loadDetail, t]);

  const salesLines = useMemo(() => {
    if (!isReport || !detail) return [];
    const fromBundle = extractDetailLines(detail.sales || {}, ['lines', 'reportSalesDetails', 'list', 'data']);
    if (fromBundle.length) return fromBundle;
    return extractDetailLines(detail, ['reportSalesDetails', 'lines', 'list', 'data']);
  }, [detail, isReport]);

  const adjustmentLines = useMemo(() => {
    if (!isReport || !detail) return [];
    const fromBundle = extractDetailLines(detail.adjustments || {}, [
      'lines',
      'reportReplenishDetail',
      'reportAdjustmentDetails',
      'list',
      'data',
    ]);
    if (fromBundle.length) return fromBundle;
    return extractDetailLines(detail, ['reportReplenishDetail', 'reportAdjustmentDetails', 'lines', 'list']);
  }, [detail, isReport]);

  const checkLines = useMemo(() => {
    if (isReport || !detail) return [];
    return extractDetailLines(detail, ['itemList', 'checkOrderDetailList', 'list', 'detailList', 'data']);
  }, [detail, isReport]);

  const statusRaw = isReport
    ? pickField(detail || {}, 'status', 'reportStatus', 'settlementStatus')
    : pickField(detail || {}, 'checkStatus', 'status');
  const statusLabel = financeStatusLabel(billType, statusRaw);

  const checkBasicRows = useMemo(() => {
    if (!detail || isReport) return [];
    const secondRaw = pickField(detail, 'secondOrderType');
    const secondName = pickField(detail, 'secondOrderTypeName');
    return [
      { label: t('finance.checkNo'), value: pickField(detail, 'checkOrderNo') || billNo },
      { label: t('finance.reportNo'), value: pickField(detail, 'reportOrderNo') },
      { label: t('finance.bizNo'), value: pickField(detail, 'bzOrderNo') },
      { label: t('finance.status'), value: statusLabel },
      {
        label: t('finance.receivable'),
        value: formatMoney(
          pickField(detail, 'estimateIncomeMoneyTotal', 'amount'),
          pickField(detail, 'currencyCode'),
        ),
      },
      { label: t('rrpOps.site'), value: pickField(detail, 'site') },
      { label: t('finance.inOut'), value: financeInOutLabel(pickField(detail, 'incomeExpenditureType')) },
      {
        label: t('finance.billType'),
        value: secondName || financeSecondOrderTypeLabel(secondRaw),
      },
      { label: t('finance.bizTime'), value: pickField(detail, 'businessCompletedTime') },
      { label: t('finance.estimatePay'), value: formatPayDate(pickField(detail, 'estimatePayTime')) },
      { label: t('finance.settled'), value: formatPayDate(pickField(detail, 'completedPayTime')) },
      { label: t('finance.originalNo'), value: pickField(detail, 'originalOrderNo') },
    ];
  }, [billNo, detail, isReport, statusLabel, t]);

  const reportBasicRows = useMemo(() => {
    if (!detail || !isReport) return [];
    return [
      { label: t('finance.reportNo'), value: pickField(detail, 'reportOrderNo') || billNo },
      { label: t('finance.status'), value: statusLabel },
      {
        label: t('finance.settledAmt'),
        value: formatMoney(
          pickField(detail, 'estimateIncomeMoneyTotal', 'amount', 'income'),
          pickField(detail, 'currencyCode'),
        ),
      },
      { label: t('finance.account'), value: pickField(detail, 'rxAcct') },
      { label: t('finance.payMethod'), value: financePaymentMethodLabel(pickField(detail, 'paymentMethod')) },
      { label: t('finance.company'), value: pickField(detail, 'companyName') },
      { label: t('finance.paidDate'), value: formatPayDate(pickField(detail, 'completedPayTime')) },
      { label: t('finance.estimatePay'), value: formatPayDate(pickField(detail, 'estimatePayTime')) },
      { label: t('finance.relatedChecks'), value: relatedChecks.length || pickField(detail, 'checkOrderCount') || 0 },
      { label: t('finance.salesCount'), value: pickField(detail, 'salesTotal') ?? salesLines.length },
      { label: t('finance.adjustCount'), value: pickField(detail, 'replenishTotal') ?? adjustmentLines.length },
    ];
  }, [
    adjustmentLines.length,
    billNo,
    detail,
    isReport,
    relatedChecks.length,
    salesLines.length,
    statusLabel,
    t,
  ]);

  const handleBack = () => navigate(buildSheinPath('finance'));
  const pageTitle = financeTabLabel(billType);

  const salesColumns = [
    { key: 'bz', label: t('finance.bizNo'), render: (line) => pickField(line, 'bzOrderNo') || '—' },
    {
      key: 'type',
      label: t('finance.type'),
      render: (line) => pickField(line, 'secondOrderTypeName')
        || financeSecondOrderTypeLabel(pickField(line, 'secondOrderType')),
    },
    {
      key: 'io',
      label: t('finance.inOut'),
      render: (line) => pickField(line, 'inAndOutName') || financeInOutLabel(pickField(line, 'inAndOut')),
    },
    {
      key: 'amt',
      label: t('finance.amount'),
      render: (line) => formatMoney(pickField(line, 'amount'), pickField(line, 'settleCurrencyCode')),
    },
    {
      key: 'merchantSku',
      label: t('certList.merchantSku'),
      render: (line) => pickField(line, 'supplierSku', 'sellerSku', 'merchantSku') || '—',
    },
    { key: 'sku', label: 'SKU', render: (line) => pickField(line, 'skuCode') || '—' },
    { key: 'time', label: t('finance.time'), render: (line) => pickField(line, 'addTime') || '—' },
  ];

  const adjColumns = [
    { key: 'bz', label: t('finance.bizNo'), render: (line) => pickField(line, 'bzOrderNo') || '—' },
    {
      key: 'cat',
      label: t('finance.category'),
      render: (line) => pickField(line, 'replenishCategory', 'secondOrderTypeName') || '—',
    },
    {
      key: 'io',
      label: t('finance.itemName'),
      render: (line) => pickField(line, 'replenishTypeName')
        || pickField(line, 'inAndOutName')
        || financeInOutLabel(pickField(line, 'inAndOut', 'replenishType')),
    },
    {
      key: 'amt',
      label: t('finance.amount'),
      render: (line) => formatMoney(pickField(line, 'amount'), pickField(line, 'settleCurrencyCode')),
    },
    { key: 'time', label: t('finance.time'), render: (line) => pickField(line, 'addTime') || '—' },
  ];

  /** 与收支明细列表一致，并额外展示每条业务单对应的对账单号 */
  const relatedCheckColumns = [
    {
      key: 'goods',
      label: t('orderList.productInfo'),
      width: '36%',
      render: (line) => <FinanceListGoodsCell goods={extractGoodsList(line)} maxVisible={null} />,
    },
    {
      key: 'bz',
      label: t('finance.bizNo'),
      width: '12%',
      render: (line) => {
        const bz = pickField(line, 'bzOrderNo') || '';
        return (
          <HoverCopyField
            className="spo-return-copy"
            value={bz}
            display={bz ? <strong>{bz}</strong> : '—'}
            emptyText="—"
          />
        );
      },
    },
    {
      key: 'check',
      label: t('finance.checkNo'),
      width: '14%',
      render: (line) => <HoverCopyField value={pickField(line, 'checkOrderNo')} emptyText="—" />,
    },
    {
      key: 'type',
      label: t('finance.billType'),
      width: '12%',
      render: (line) => (
        <span className="spo-finance-bill-type">
          {financeSecondOrderTypeLabel(pickField(line, 'secondOrderType'))}
        </span>
      ),
    },
    {
      key: 'io',
      label: t('finance.inOut'),
      width: '8%',
      render: (line) => {
        const raw = pickField(line, 'incomeExpenditureType');
        return (
          <span className={`spo-finance-inout spo-finance-inout--${Number(raw) === 2 ? 'out' : 'in'}`}>
            {financeInOutLabel(raw)}
          </span>
        );
      },
    },
    {
      key: 'pay',
      label: t('finance.estimatePay'),
      width: '10%',
      render: (line) => formatPayDate(
        pickField(line, 'estimatePayTime') || pickField(line, 'completedPayTime'),
      ),
    },
    {
      key: 'amt',
      label: t('finance.receivable'),
      width: '8%',
      render: (line) => {
        const ledger = formatLedgerAmount(
          pickField(line, 'amount', 'estimateIncomeMoneyTotal'),
          pickField(line, 'currencyCode'),
          pickField(line, 'incomeExpenditureType'),
        );
        return (
          <span className={`spo-finance-amt spo-finance-amt--${ledger.tone}`}>{ledger.text}</span>
        );
      },
    },
  ];

  const checkLineColumns = [
    {
      key: 'goods',
      label: t('finance.goodsInfo'),
      render: (line) => <FinanceSkuGoodsCell line={line} />,
    },
    {
      key: 'qty',
      label: t('finance.qty'),
      render: (line) => {
        const qty = pickField(line, 'quantity', 'qty', 'goodsNum');
        return qty == null || qty === '' ? '—' : String(qty);
      },
    },
    {
      key: 'price',
      label: t('finance.salePrice'),
      render: (line) => formatMoney(pickField(line, 'sellerCurrencyPrice'), pickField(detail, 'currencyCode')),
    },
    {
      key: 'income',
      label: t('finance.estimateIncome'),
      render: (line) => (
        <span className="spo-finance-amt spo-finance-amt--link">
          {formatMoney(pickField(line, 'incomeAmount'), pickField(detail, 'currencyCode'))}
        </span>
      ),
    },
    {
      key: 'comm',
      label: t('finance.commission'),
      render: (line) => formatMoney(pickField(line, 'commissionAmount'), pickField(detail, 'currencyCode')),
    },
    {
      key: 'perf',
      label: t('finance.fulfillFee'),
      render: (line) => formatMoney(
        pickField(line, 'performanceCost', 'showPerformanceCost'),
        pickField(detail, 'currencyCode'),
      ),
    },
  ];

  const showFullLineDetails = salesLines.length > 0 || adjustmentLines.length > 0
    || Boolean(detail?.sales) || Boolean(detail?.adjustments);

  return (
    <div className="spo-return-detail spo-finance-detail" data-testid="shein-finance-bill-detail">
      <nav className="spo-return-detail__crumb" aria-label={t('finance.crumb')}>
        <button type="button" className="spo-return-detail__crumb-link" onClick={handleBack}>
          {t('platformNav.finance')}
        </button>
        <span className="spo-return-detail__crumb-sep">/</span>
        <span>{pageTitle}</span>
      </nav>

      <header className="spo-return-detail__header">
        <div className="spo-return-detail__title-block">
          <h1 className="spo-return-detail__title">{pageTitle}</h1>
          <div className="spo-return-detail__meta">
            <strong className="spo-return-detail__no">{billNo || '—'}</strong>
            {detail ? (
              <span className={`spo-records__status spo-records__status--${financeStatusModifier(statusRaw)}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
        </div>
        <div className="spo-return-detail__actions">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={handleBack}>
            {t('finance.back')}
          </button>
        </div>
      </header>

      {loading && !detail ? (
        <p className="spo-return-detail__loading">{t('returnList.loading')}</p>
      ) : null}
      {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}

      {!loading && !error && !detail ? (
        <p className="spo-return-detail__empty">{t('finance.noDetail', { platform: 'SHEIN' })}</p>
      ) : null}

      {detail ? (
        <div className="spo-return-detail__body">
          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('finance.basic')}</h2>
            <FinanceKvGrid rows={isReport ? reportBasicRows : checkBasicRows} />
          </section>

          {isReport ? (
            <>
              <section className="spo-return-detail__section">
                <h2 className="spo-return-detail__section-title">
                  {t('finance.ledgerTitle')}
                  <span className="spo-return-detail__section-count">{relatedChecks.length}</span>
                </h2>
                <LinesTable
                  className="spo-finance-detail__check-list"
                  columns={relatedCheckColumns}
                  rows={relatedChecks}
                  emptyText={t('finance.noRelated')}
                />
              </section>
              {showFullLineDetails ? (
                <>
                  <section className="spo-return-detail__section">
                    <h2 className="spo-return-detail__section-title">
                      {t('finance.salesTitle')}
                      <span className="spo-return-detail__section-count">{salesLines.length}</span>
                    </h2>
                    <LinesTable
                      columns={salesColumns}
                      rows={salesLines}
                      emptyText={detail?.sales?.error || t('finance.noSales')}
                    />
                  </section>
                  <section className="spo-return-detail__section">
                    <h2 className="spo-return-detail__section-title">
                      {t('finance.adjustTitle')}
                      <span className="spo-return-detail__section-count">{adjustmentLines.length}</span>
                    </h2>
                    <LinesTable
                      columns={adjColumns}
                      rows={adjustmentLines}
                      emptyText={detail?.adjustments?.error || t('finance.noAdjust')}
                    />
                  </section>
                </>
              ) : null}
            </>
          ) : (
            <section className="spo-return-detail__section">
              <h2 className="spo-return-detail__section-title">
                {t('finance.skuTitle')}
                <span className="spo-return-detail__section-count">{checkLines.length}</span>
              </h2>
              <LinesTable
                columns={checkLineColumns}
                rows={checkLines}
                emptyText={t('finance.noSku', { platform: 'SHEIN' })}
              />
            </section>
          )}
        </div>
      ) : null}
    </div>
  );
}
